import { Component } from '@theme/component';

/**
 * A single UGC video review card: play/pause + mute toggles, optional muted autoplay while in view.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} [video] - The review video. Absent when only a poster image is set.
 * @property {HTMLButtonElement} [playButton] - Toggles playback.
 * @property {HTMLButtonElement} [muteButton] - Toggles sound.
 *
 * @extends Component<Refs>
 */
class VideoReviewComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  #setup() {
    this.#teardown();
    const { video } = this.refs;
    if (!video) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    video.addEventListener('play', this.#sync, { signal });
    video.addEventListener('pause', this.#sync, { signal });
    video.addEventListener('volumechange', this.#sync, { signal });

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.hasAttribute('data-autoplay') && !reducedMotion) {
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          if (entry.isIntersecting && video.muted) {
            video.play().catch(() => {});
          } else if (!entry.isIntersecting && video.muted) {
            video.pause();
          }
        },
        { threshold: 0.6 }
      );
      this.#observer.observe(video);
    }
    this.#sync();
  }

  #teardown() {
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  togglePlay() {
    const { video } = this.refs;
    if (!video) return;
    if (video.paused) {
      // Pause any other review that is playing with sound so two never overlap.
      for (const other of document.querySelectorAll('video-review-component video')) {
        if (other !== video && other instanceof HTMLVideoElement && !other.muted) other.pause();
      }
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  toggleMute() {
    const { video } = this.refs;
    if (!video) return;
    video.muted = !video.muted;
    if (!video.muted && video.paused) this.togglePlay();
  }

  #sync = () => {
    const { video, playButton, muteButton } = this.refs;
    if (!video) return;
    this.toggleAttribute('data-playing', !video.paused);
    playButton?.setAttribute('aria-pressed', String(!video.paused));
    muteButton?.setAttribute('aria-pressed', String(video.muted));
  };
}

if (!customElements.get('video-review-component')) {
  customElements.define('video-review-component', VideoReviewComponent);
}
